import useOnClickOutside from '@/hooks/useOnClickOutside';
import React, {
	HTMLProps,
	MouseEventHandler,
	PropsWithChildren,
	ReactNode,
	useRef,
	useState
} from 'react';
import { uid } from 'react-uid';
import './ContextMenu.scss';

export type MouseHandler = MouseEventHandler & {
	(e: React.MouseEvent | MouseEvent): void;
};

export type PositionHandler = (position: { top: number; left: number }) => void;

export type ContextMenuHandler = MouseHandler | PositionHandler;

type Props = Omit<HTMLProps<HTMLElement>, 'onSelect'> &
	PropsWithChildren<{
		onSelect: (item: string) => void;
		offset?: {
			top?: number;
			right?: number;
			bottom?: number;
			left?: number;
		};
		items: string[];
		disabled?: boolean;
		children(handleContextMenu: ContextMenuHandler): ReactNode;
	}>;

export default function ContextMenu({
	onSelect,
	offset = {
		top: 0,
		right: 0,
		bottom: 0,
		left: 0
	},
	items,
	disabled,

	children
}: Props): JSX.Element {
	const menuRef = useRef<HTMLDivElement>(null);

	const menuLeft = useRef<number>();
	const menuTop = useRef<number>();

	const [show, setShow] = useState<boolean>(false);

	useOnClickOutside({
		ref: menuRef,
		handler: () => setShow(false)
	});

	const handleContextMenu = (e: React.MouseEvent | MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const element = e.currentTarget as HTMLElement;

		if (element) {
			const elementBounds = element?.getBoundingClientRect();

			menuLeft.current =
				e.pageX - elementBounds?.left + (offset.left ?? 0);
			menuTop.current = e.pageY - elementBounds?.top + (offset.top ?? 0);

			setShow(true);
		}
	};

	return (
		<>
			{show && !disabled && (
				<div
					ref={menuRef}
					style={{
						left: menuLeft.current,
						top: menuTop.current
					}}
					className="contextMenu absolute z-50 inline-flex flex-col p-2"
				>
					{items.map((item, index) => (
						<button
							key={uid(item, index)}
							type="button"
							onClick={(e: React.MouseEvent | MouseEvent) => {
								e.stopPropagation();
								e.preventDefault();

								onSelect(`[${item}]`);

								setShow(false);
							}}
						>
							[{item}]
						</button>
					))}
				</div>
			)}
			{children(handleContextMenu)}
		</>
	);
}
